import { ArrowSquareOut } from '@phosphor-icons/react';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getActiveGroups, getProfileDisplaysForProfile, moveGroupProfiles } from '../service';
import { moveGroupProfileSchema } from '../utils/schema';
import { Checkbox, RadioSelect } from './common';
import Button from './common/button';

export default function MoveProfile({
  profiles = [],
  groupId,
  groupName = '',
  onSubmit = () => {}
}) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [passiveDisplaysList, setPassiveDisplaysList] = useState([]);

  const fetchGroups = async () => {
    try {
      const { data: groupsList } = await getActiveGroups();
      // Filter out the current group from the list
      const filteredGroups = groupsList.filter((group) => group._id !== groupId);

      const selectedFormattedGroups = filteredGroups.map((group) => ({
        _id: group._id,
        label: group.name,
        value: group._id
      }));

      setGroups(selectedFormattedGroups);
      if (selectedFormattedGroups.length > 0) {
        formik.setFieldValue('group', selectedFormattedGroups[0].value);
      }
    } catch (error) {
      toast.error('Failed to get groups');
      console.error('Failed to get groups: ', error);
    }
  };

  const fetchProfileDisplaysForProfile = async (profileId) => {
    try {
      const { data: profileDisplays } = await getProfileDisplaysForProfile(profileId);
      console.log('Profile displays:', profileDisplays);

      // Handle activeDisplay as object with _id and name
      if (profileDisplays.activeDisplay) {
        formik.setFieldValue('group', profileDisplays.activeDisplay._id);
      }

      // Store passive displays for display purposes
      if (profileDisplays.passiveDisplays?.length) {
        setPassiveDisplaysList(profileDisplays.passiveDisplays);

        // Extract just the IDs from passiveDisplays
        const passiveDisplayIds = profileDisplays.passiveDisplays.map((display) => display._id);
        console.log('Passive display IDs:', passiveDisplayIds);

        // Check if current group is in passive displays
        const isPassiveInCurrentGroup = passiveDisplayIds.includes(groupId);
        formik.setFieldValue('isPassiveInCurrentGroup', isPassiveInCurrentGroup);
        formik.setFieldValue('passiveDisplays', isPassiveInCurrentGroup ? [groupId] : []);
      }
    } catch (error) {
      toast.error('Failed to get profile displays');
      console.error('Failed to get profile displays: ', error);
    }
  };

  const formik = useFormik({
    initialValues: {
      group: '',
      profiles,
      passiveDisplays: [],
      resetProgress: false,
      isPassiveInCurrentGroup: false
    },
    validationSchema: moveGroupProfileSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const { group, profiles, resetProgress, passiveDisplays } = values;
        const moveToGroupId = group;

        await moveGroupProfiles(groupId, profiles, moveToGroupId, resetProgress, passiveDisplays);
        toast.success(t('moveProfiles.success.PROFILES_MOVED_SUCCESSFULLY'));
        const profileIds = moveToGroupId !== groupId ? profiles.map((profile) => profile._id) : [];
        onSubmit(profileIds, values.isPassiveInCurrentGroup);
      } catch (error) {
        console.error('Failed to move profile/s:', error);
        if (error?.response?.data?.message) {
          toast.error(t(error.response.data.message));
        } else {
          toast.error(t('moveProfiles.errors.GENERIC_FAILED_TO_MOVE_PROFILES'));
        }
      }
    }
  });

  const handlePassiveCheckboxChange = () => {
    const newValue = !formik.values.isPassiveInCurrentGroup;
    formik.setFieldValue('isPassiveInCurrentGroup', newValue);
    // Update passiveDisplays immediately based on checkbox state
    formik.setFieldValue('passiveDisplays', newValue ? [groupId] : []);
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (profiles.length === 1) {
      fetchProfileDisplaysForProfile(profiles[0]._id);
    }
  }, [groups]);

  return (
    <div>
      <header className="bg-[#F8F8F8] px-6 py-4 rounded-t-lg flex flex-col items-start justify-start">
        <h4 className="font-semibold">{t('moveProfiles.title')}</h4>
        <p className="text-sm">
          {t('moveProfiles.from')} <span className="font-semibold underline">{groupName}</span>
        </p>
      </header>
      <main className="p-6">
        <form onSubmit={formik.handleSubmit}>
          <p className="font-semibold">{t('moveProfiles.activeDisplayTitle')}</p>
          <RadioSelect
            name="group"
            label={t('moveProfiles.moveField')}
            className="my-1"
            value={formik.values.group}
            touched={formik.touched.group}
            onBlur={formik.handleBlur}
            error={formik.errors.group}
            options={groups}
            onChange={(option) => {
              formik.setFieldValue('group', option, true);
            }}
          />
          <div className="mt-4 mb-4">
            <Checkbox
              value="resetProgress"
              label={t('moveProfiles.resetProgress')}
              description={t('moveProfiles.resetProgressDescription')}
              enabled={formik.values.resetProgress}
              onChange={() => formik.setFieldValue('resetProgress', !formik.values.resetProgress)}
            />
          </div>
          <div className="mt-4 mb-2">
            <Checkbox
              value="isPassiveInCurrentGroup"
              label={t('moveProfiles.keepProfilePassiveInCurrentGroup')}
              description={t('moveProfiles.keepProfilePassiveInCurrentGroupDescription')}
              enabled={formik.values.isPassiveInCurrentGroup}
              onChange={handlePassiveCheckboxChange}
            />
          </div>

          {profiles.length === 1 && passiveDisplaysList.length > 0 && (
            <>
              <hr className="h-px my-8 bg-gray-200 border-0 dark:bg-gray-700"></hr>
              <div className="mt-4 mb-6">
                <p className="font-semibold">{t('moveProfiles.passiveDisplaysTitle')}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {passiveDisplaysList.map((display) => (
                    <a
                      key={display._id}
                      href={`/${display._id}/profiles`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
                      title="Passive Display">
                      {display.name}
                      <ArrowSquareOut size={12} color="#9747FF" />
                    </a>
                  ))}
                </div>
              </div>
            </>
          )}

          <footer className="flex item-center justify-end mt-6">
            <Button.ButtonFilled type="submit" isMini loading={formik.isSubmitting}>
              {t('moveProfiles.submit')}
            </Button.ButtonFilled>
          </footer>
        </form>
      </main>
    </div>
  );
}
